// HeyMotek — CRM marketplace install landing (PLAN_GHL_MVP.md P1 + addendum).
//
// The CRM redirects here after the app install: /crm/install?code=<one-use>.
// The code is short-lived → exchange IMMEDIATELY (unauthenticated), park the
// claim in sessionStorage, then show the signup form ON THIS PAGE (activate
// framing — no hop). After auth, the claim links location ↔ client and the
// success screen sends them into onboarding.
//
// Design locked 2026-07-30 (audited same day): a single clean card per state
// — the activation screen has ONE job (press the Google button). No proof
// panel: post-install visitors need orientation, not persuasion, and the
// white-label rule bars real platform branding ("your CRM" in copy, never
// "GHL"). Installer email/name from the exchange are a HINT
// (prefill/greeting), never an auth key.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/firebase';
import { ApiError, apiRequest } from '@/lib/api';
import { BackdropDashboard } from '@/components/auth/BackdropDashboard';
import { SignupForm } from '@/components/auth/SignupForm';

const PENDING_KEY = 'ghl_pending_install';

interface PendingInstall {
  claim_token: string;
  locations: { location_id: string; name: string | null; already_linked: boolean }[];
  installer?: { email?: string | null; name?: string | null } | null;
}

type Phase =
  | { step: 'exchanging' }
  | { step: 'needs_auth'; pending: PendingInstall }
  | { step: 'pick_location'; pending: PendingInstall }
  | { step: 'claiming' }
  | { step: 'done'; locationName: string | null; already?: boolean }
  | { step: 'error'; message: string };

function loadPending(): PendingInstall | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingInstall) : null;
  } catch {
    return null;
  }
}

function claimErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400)
      return "You're signed in with an admin account. Log out, then create (or sign in with) the account that should own this location.";
    if (err.status === 409)
      return 'This HeyMotek account is already linked to another location. Each account tracks one location — create a separate account for this one.';
    if (err.status === 404)
      return 'This install link expired or was already used. Open the app install from your CRM again.';
  }
  return "Couldn't link the location. Re-run the install from your CRM and try again.";
}

// ─── Page ───────────────────────────────────────────────────────────

export function GhlInstallPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ step: 'exchanging' });
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const ranRef = useRef(false);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  // Entry: a fresh ?code= from the CRM, or a return visit with a pending
  // install in sessionStorage (post signup/login).
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    const stored = loadPending();

    async function exchange(freshCode: string) {
      try {
        const redirectUri = `${window.location.origin}/crm/install`;
        const pending = await apiRequest<PendingInstall>('/ghl/exchange', {
          method: 'POST',
          body: { code: freshCode, redirect_uri: redirectUri },
        });
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        // Drop the single-use code from the URL so refresh doesn't re-exchange.
        window.history.replaceState({}, '', '/crm/install');
        setPhase({ step: 'needs_auth', pending });
      } catch {
        setPhase({
          step: 'error',
          message:
            "Couldn't complete the install handshake. Re-open the app install from your CRM and try again.",
        });
      }
    }

    if (code) void exchange(code);
    else if (stored) setPhase({ step: 'needs_auth', pending: stored });
    else
      setPhase({
        step: 'error',
        message: 'No install in progress. Start from the app install link in your CRM.',
      });
  }, []);

  // Once we have both a pending install and a signed-in user → claim.
  // Already-linked locations are still claimed: the backend is idempotent for
  // the owner (re-install / version update → success screen), 409 otherwise.
  useEffect(() => {
    if (phase.step !== 'needs_auth' || !user) return;
    const claimable = phase.pending.locations.filter((l) => !l.already_linked);
    if (claimable.length === 0) {
      void claim(phase.pending, phase.pending.locations[0].location_id);
      return;
    }
    if (claimable.length > 1) {
      setPhase({ step: 'pick_location', pending: phase.pending });
      return;
    }
    void claim(phase.pending, claimable[0].location_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.step, user]);

  async function claim(pending: PendingInstall, locationId: string) {
    setPhase({ step: 'claiming' });
    try {
      const res = await apiRequest<{
        location_id: string;
        location_name: string | null;
        already?: boolean;
      }>('/ghl/claim', {
        method: 'POST',
        body: { claim_token: pending.claim_token, location_id: locationId },
      });
      sessionStorage.removeItem(PENDING_KEY);
      setPhase({ step: 'done', locationName: res.location_name, already: res.already });
    } catch (err) {
      setPhase({ step: 'error', message: claimErrorMessage(err) });
    }
  }

  const installer = phase.step === 'needs_auth' || phase.step === 'pick_location' ? phase.pending.installer : null;
  const firstName = installer?.name?.split(' ')[0] || null;

  // Centered single-card states (spinner / success / error / picker)
  function CenterCard({ children }: { children: React.ReactNode }) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10 text-center">
          <div className="mb-6">
            <span className="font-extrabold text-2xl tracking-tight">
              <span style={{ WebkitTextStroke: '2px #0F172A', color: 'white', paintOrder: 'stroke fill' }}>hey</span>
              <span className="text-brand">motek</span>
            </span>
          </div>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-tint min-h-screen relative">
      <BackdropDashboard />

      {phase.step === 'exchanging' && (
        <CenterCard>
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p>Finishing the install…</p>
          </div>
        </CenterCard>
      )}

      {/* Activation screen — single clean card, one job: press the button.
          (Two-column proof panel removed 2026-07-30 — post-install visitors
          need orientation, not persuasion; a generic "Your CRM" tile carries
          no recognition and we can't show real platform branding.) */}
      {phase.step === 'needs_auth' && !user && (
        <div className="relative z-10 min-h-screen flex items-center justify-center p-4 md:p-6">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="mb-6 text-center">
              <span className="font-extrabold text-2xl tracking-tight">
                <span style={{ WebkitTextStroke: '2px #0F172A', color: 'white', paintOrder: 'stroke fill' }}>hey</span>
                <span className="text-brand">motek</span>
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3 leading-tight text-center">
              Almost there{firstName ? `, ${firstName}` : ''} <span className="inline-block">🎉</span>
            </h1>
            <p className="text-base leading-relaxed mb-8 text-center">
              <span className="font-semibold">The app is installed on your CRM.</span>{' '}
              <span className="text-muted-foreground">Create your account to activate it.</span>
            </p>

            <SignupForm
              emphasizeGoogle
              prefillEmail={installer?.email ?? null}
              submitLabel="Activate my account"
              onComplete={() => {
                /* auth-state effect picks up the user and runs the claim */
              }}
            />

            {/* Sign-in — deliberately small: installers are ~all new users */}
            <p className="pt-8 text-center text-sm text-muted-foreground">
              Already a <span className="font-bold text-brand">Motek</span>?{' '}
              <a href="/signin" className="font-bold text-foreground hover:text-brand">
                Sign in here
              </a>
              , then return to this tab.
            </p>
          </div>
        </div>
      )}

      {phase.step === 'pick_location' && (
        <CenterCard>
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Pick the location</h1>
          <p className="text-muted-foreground mb-6">
            This HeyMotek account tracks one location. Choose which one to link:
          </p>
          <div className="space-y-2">
            {phase.pending.locations
              .filter((l) => !l.already_linked)
              .map((l) => (
                <Button
                  key={l.location_id}
                  variant="outline"
                  className="w-full h-12 text-base justify-start"
                  onClick={() => void claim(phase.pending, l.location_id)}
                >
                  {l.name || l.location_id}
                </Button>
              ))}
          </div>
        </CenterCard>
      )}

      {phase.step === 'claiming' && (
        <CenterCard>
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p>Linking your location…</p>
          </div>
        </CenterCard>
      )}

      {phase.step === 'done' && (
        <CenterCard>
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-6 h-6 text-green-700" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">
            {phase.locationName
              ? `${phase.locationName} ${phase.already ? 'is connected' : 'linked'}`
              : phase.already ? 'Already connected' : 'Location linked'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {phase.already
              ? 'Everything is up to date — the app update is live on this location.'
              : "Now connect WhatsApp and your ad accounts — that's what turns messages into attributed customers."}
          </p>
          <Button
            className="w-full h-12 text-base font-bold"
            onClick={() => navigate(phase.already ? '/' : '/onboarding')}
          >
            {phase.already ? 'Go to dashboard' : 'Continue setup'}
          </Button>
        </CenterCard>
      )}

      {phase.step === 'error' && (
        <CenterCard>
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Something's off</h1>
          <p className="text-muted-foreground mb-6">{phase.message}</p>
          <Button variant="outline" className="w-full h-12 text-base" onClick={() => navigate('/')}>
            Go to dashboard
          </Button>
        </CenterCard>
      )}
    </div>
  );
}
